import { Box, Skeleton, Typography } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { Reference } from 'sr-types/lib/search/v1/graphql';
import { getFullName } from '../../../../../person/person';
import RoundAvatar from '../../../../../production/header/RoundAvatar';
import EditablePanel, { EditablePanelProps } from '../../../../components/EditablePanel';
import MultipleChipPreview, { MultipleChipPreviewProps } from '../../../../components/MultipleChipPreview';
import { ENTITY_CONSTANTS } from '../../../../constants/entityConstants';
import I18n from '../../../../i18n/I18n';
import { useStyling } from '../../../../Theme';
import { APPROVAL_EDITABLE_PANEL_CONTENT_SX } from '../../../constants';
import { useReviewApprovalContext } from '../../hooks/useReviewApprovalContext';
import CreateApprovalFormEditablePanelHeader from '../CreateApprovalFormEditablePanelHeader';

const chipLabelProps: MultipleChipPreviewProps['chipLabelProps'] = { sx: { fontSize: 'body1.fontSize' } };
const headerActionsSx = { flex: 1 };

export type ApprovalNotifyContactsViewProps = Pick<EditablePanelProps, 'isCollapsable' | 'defaultCollapsed'>;

export default function ApprovalNotifyContactsView({
    isCollapsable = true,
    defaultCollapsed = false
}: ApprovalNotifyContactsViewProps) {
    const { theme, isDarkMode } = useStyling();

    const { approvalData, isFetchingApprovalData } = useReviewApprovalContext();

    const contactReferences: Reference[] = useMemo(
        () =>
            approvalData?.notifyContacts?.map((contact, contactIndex) => ({
                id: contact.personReference?.id ?? `temp-contact-${contactIndex}`,
                label: getFullName(contact.name),
                type: ENTITY_CONSTANTS.person
            })) || [],
        [approvalData?.notifyContacts]
    );

    const contactAvatarRenderer = useCallback(
        (contactReference: Reference) => (
            <RoundAvatar
                title={contactReference.label}
                bgColor={isDarkMode ? theme.palette.grey[700] : theme.palette.grey[300]}
            />
        ),
        [isDarkMode, theme.palette.grey]
    );

    let notifyContactsContent = null;
    if (isFetchingApprovalData) {
        notifyContactsContent = (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {Array.from({ length: 3 }).map((_, skeletonIndex) => (
                    <Skeleton key={skeletonIndex} variant="rounded" width={140} height={32} sx={{ borderRadius: 32 }} />
                ))}
            </Box>
        );
    } else if (contactReferences.length === 0) {
        notifyContactsContent = (
            <Typography
                color="textSecondary"
                sx={{ py: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}
            >
                <I18n token="approval.notify.noContacts" />
            </Typography>
        );
    } else {
        notifyContactsContent = (
            <MultipleChipPreview
                chipLabelProps={chipLabelProps}
                data={contactReferences}
                chipIconRenderer={contactAvatarRenderer}
            />
        );
    }

    return (
        <EditablePanel
            Header={<CreateApprovalFormEditablePanelHeader titleToken="approval.notify.header" />}
            isCollapsable={isCollapsable}
            defaultCollapsed={defaultCollapsed}
            headerActionsSx={headerActionsSx}
        >
            <Box sx={APPROVAL_EDITABLE_PANEL_CONTENT_SX}>{notifyContactsContent}</Box>
        </EditablePanel>
    );
}
